import BundleSummaryBlock from '@/components/BundleSummaryBlock/BundleSummaryBlock';
import { SummaryBlockVariants } from '@/components/BundleSummaryBlock/BundleSummaryBlock.types';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import Icon from '@/components/Icons/Icon';
import Link from '@/components/Link/Link';
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal';
import Table from '@/components/Tables/Table/Table';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import BundleDetailsPlaceholder from '@/images/BundleDetailsPlaceholder.svg';
import EditLinkedBundlesModal from '@/pages/Admin/Bundles/BundleDetails/EditLinkedBundlesModal/EditLinkedBundlesModal';
import {
  useBundleDetails,
  useBundleStatistics,
  useMaintenanceFreezeDetails,
  useNationalProgramCurrent,
  useUpdateBundleVisibility,
  useUpdateBundleLock,
} from '@/services/bundles/bundles.service';
import { BundleStatisticsTable } from '@/types/bundles.types';
import { NationalProgramStates } from '@/types/nationalProgram.types';
import { renderLockedSettings, renderOccasions } from '@/utils/formatting/formatBundleProperties';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './BundleDetails.module.css';
import CreateFreeze from './CreateFreeze/CreateFreeze';
import EditBundle from './EditBundle/EditBundle';
import FreezeDetails from './FreezeDetails/FreezeDetails';
import { IconLinkedBundleText } from './Internal/IconLinkedBundleText';
import SetVisibleModal from './SetVisibleModal/SetVisibleModal';
import LockBundleModal from './LockBundleModal/LockBundleModal';
import StorePricesModal from './StorePricesModal/StorePricesModal';
import { CellContext } from '@tanstack/react-table';

const storeCountCell = (storesCountOnClick: (price: string) => void) => {
  const Cell = ({ row }: CellContext<BundleStatisticsTable, unknown>) => (
    <Button
      onClick={() => !!row.original.storeCount && storesCountOnClick(row.original.price)}
      variant={ButtonVariants.Text}
    >
      {row.original.storeCount || '0'}
    </Button>
  );
  return Cell;
};

const BundleDetails: FC = () => {
  const { t } = useTranslation();
  const { bundleId } = useParams();
  const navigateWithToast = useNavigateWithToast();

  const [showEditBundle, setShowEditBundle] = useState(false);
  const [pricesModalConfig, setPricesModalConfig] = useState({} as { openModal: boolean; pricePoint: string });
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [freezeName, setFreezeName] = useState('');

  const {
    data: bundleDetailsData,
    isLoading: bundleDetailsLoading,
    isError: bundleDetailsError,
    refetch: refetchBundleDetails,
  } = useBundleDetails(bundleId !== undefined, bundleId!);
  const { data: nationalProgramData } = useNationalProgramCurrent(bundleId !== undefined, bundleId!);
  const { data: maintenanceFreezeData } = useMaintenanceFreezeDetails(bundleId !== undefined, bundleId!);
  const {
    mutate: updateBundleVisibility,
    isLoading: updateBundleVisibilityLoading,
    reset: updateBundleVisibilityReset,
  } = useUpdateBundleVisibility(bundleId!);

  const {
    mutate: updateBundleLock,
    isLoading: updateBundleLockLoading,
    reset: updateBundleLockReset,
  } = useUpdateBundleLock(bundleId!);

  const {
    data: bundleStatisticsData,
    isLoading: bundleStatisticsLoading,
    isError: bundleStatisticsError,
  } = useBundleStatistics(bundleId !== undefined, bundleId!);

  const allPricesData: BundleStatisticsTable[] = [];
  const [isEditLinkedBundlesModalOpen, setIsEditLinkedBundleSettingsModalOpen] = useState<boolean>(false);
  if (bundleDetailsError) {
    navigateWithToast('/', {
      label: t('error.fetchingBundleDetails'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingBundleDetails',
    });
  }

  if (bundleStatisticsError) {
    navigateWithToast('/', {
      label: t('error.fetchingBundleStatistics'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingBundleStatistics',
    });
  }

  const recommendedPricesStatistics: BundleStatisticsTable[] = (
    bundleStatisticsData?.data?.recommendedPrices || []
  ).map((item: any) => ({
    price: `$${item.price ? item.price.toFixed(2) : ' -'}`,
    storeCount: item.storeCount,
    type: t('editBundle.priceStats.recommended'),
  }));

  const availablePricesStatistics: BundleStatisticsTable[] = (bundleStatisticsData?.data?.availablePrices || []).map(
    (item: any) => ({
      price: `$${item.price ? item.price.toFixed(2) : '-'}`,
      storeCount: item.storeCount,
      type: ' ',
    }),
  );

  const legacyPricesStatistics: BundleStatisticsTable[] = (bundleStatisticsData?.data?.legacyPrices || []).map(
    (item: any) => ({
      price: `$${item.price ? item.price.toFixed(2) : '-'}`,
      storeCount: item.storeCount,
      type: t('editBundle.priceStats.legacy'),
    }),
  );

  allPricesData.push(...recommendedPricesStatistics);
  allPricesData.push(...availablePricesStatistics);
  allPricesData.push(...legacyPricesStatistics);

  const displayName = bundleDetailsData?.data?.name || '-';
  const bundleCode = bundleDetailsData?.data?.code || '-';
  const bundleDesc = bundleDetailsData?.data?.internalName || '-';
  const availablePrices = bundleDetailsData?.data?.availablePrices || [];
  const recommendedPrices = bundleDetailsData?.data?.recommendedPrices || [];
  const formattedAvailablePrices = availablePrices?.map((price) => `$${price?.toFixed(2)}`).join(', ') || '-';
  const formattedRecommendedPrices =
    recommendedPrices
      ?.filter((price) => price || price === 0)
      ?.map((price) => `$${price?.toFixed(2)}`)
      .join(', ') || '-';
  const occasion = renderOccasions(t, bundleDetailsData?.data?.occasions);
  const lockedSettingsDescription = renderLockedSettings(bundleDetailsData?.data);

  const productChoicesData = bundleDetailsData?.data?.choices?.map((choice) => ({
    choiceName: choice.choice.internalName,
    defaultProduct: choice.defaultVariants.length === 0 ? '-' : choice.defaultVariants[0].product?.internalName,
    defaultVariant:
      choice.defaultVariants.length === 0
        ? '-'
        : choice.defaultVariants[0].selectedOptions?.map((option) => option.optionValue.internalName).join(', '),
  }));

  const npIsLiveOrOptIn =
    nationalProgramData?.data.state === NationalProgramStates.LIVE ||
    nationalProgramData?.data.state === NationalProgramStates.OPT_IN;

  const isMaintenanceFreeze = maintenanceFreezeData?.data?.bundleFrozen || false;

  const editButtonIsVisible = !npIsLiveOrOptIn && !isMaintenanceFreeze && availablePrices?.length > 0;

  const isMissingPricePoints = !bundleDetailsLoading && availablePrices?.length === 0;

  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showLockBundleModal, setShowLockBundleModal] = useState(false);

  const openCreateWizard = () => setShowCreateWizard(true);
  const closeCreateWizard = () => setShowCreateWizard(false);

  const viewNavigationCodes = () => {
    window.location.href = `/bundles/${bundleId}/viewNavCode`;
  };

  const handleEditClick = () => {
    setShowEditBundle(true);
  };

  const openVisibilityModal = () => {
    setShowVisibilityModal(true);
  };

  const openLinkedBundleModal = () => {
    setIsEditLinkedBundleSettingsModalOpen(true);
  };

  const closeVisibilityModal = () => {
    setShowVisibilityModal(false);
  };

  const handleSetVisibilityClick = () => {
    updateBundleVisibility(
      {
        isVisible: !bundleDetailsData?.data?.isVisible,
      },
      {
        onSuccess: () => {
          showToast({
            label: t('editBundle.bundleDetails.bundleVisibilityUpdated'),
            id: 'editBundleVisibilitySuccess',
          });
          closeVisibilityModal();
        },
        onError: () => {
          showToast({
            label: t('error.updatingBundle'),
            toastType: ToastTypes.Error,
            id: 'errorEditingBundleVisibility',
          });
          closeVisibilityModal();
          updateBundleVisibilityReset();
        },
      },
    );
  };

  const handleLockBundleClick = () => {
    updateBundleLock(
      {
        bundleCode: bundleDetailsData!.data.code,
      },
      {
        onSuccess: () => {
          showToast({
            label: bundleDetailsData?.data?.isLocked
              ? t('editBundle.bundleDetails.bundleUnlockSuccess')
              : t('editBundle.bundleDetails.bundleLockSuccess'),
            id: 'editBundleLockSuccess',
          });
          setShowLockBundleModal(false);
          updateBundleLockReset();
        },
        onError: () => {
          showToast({
            label: bundleDetailsData?.data?.isLocked
              ? t('editBundle.bundleDetails.bundleUnlockError')
              : t('editBundle.bundleDetails.bundleLockError'),
            toastType: ToastTypes.Error,
            id: 'editBundleLockFailure',
          });
          setShowLockBundleModal(false);
          updateBundleLockReset();
        },
      },
    );
  };

  const handleLinkedBundleClose = () => {
    setIsEditLinkedBundleSettingsModalOpen(false);
  };

  useEffect(() => {
    setIsEditLinkedBundleSettingsModalOpen(false);
  }, [bundleId]);

  const openModalStoreList = (price: string) => {
    setPricesModalConfig({ openModal: true, pricePoint: price });
  };

  return showEditBundle ? (
    <EditBundle
      postDeleteBundleData={() => refetchBundleDetails()}
      bundleData={bundleDetailsData?.data}
      setShowEditBundle={setShowEditBundle}
      bundleId={bundleId}
    />
  ) : (
    <>
      <div style={{ minWidth: 'fit-content' }}>
        <Header
          title={bundleCode !== '-' && displayName !== '-' ? `${bundleCode} - ${displayName}` : ''}
          breadcrumb={{ label: t('breadcrumb.backToAllBundles'), to: '/bundles/library' }}
          additionalTitleInfo={bundleDetailsData?.data?.bundleLinkedToDto && <IconLinkedBundleText />}
          onClickAdditional={() => openLinkedBundleModal()}
        />
      </div>

      <div className={styles.page}>
        <div className={classNames(styles.topContainer, styles.whiteBackground)}>
          {isMissingPricePoints && (
            <Card className={styles.tooltipCard}>
              <Icon iconName="Info" className={styles.iconButton} />
              <div>{t('editBundle.noPricesDefined')}</div>
            </Card>
          )}
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              <h2>{t('editBundle.bundleDetails.title')}</h2>
              <Tag
                type={bundleDetailsData?.data?.isVisible ? TagTypes.Completed : TagTypes.Inactive}
                label={bundleDetailsData?.data?.isVisible ? t('common.visible') : t('common.invisible')}
              />
            </div>
            <div className={styles.visibleAndLockButtonsWrapper}>
              <Button
                variant={bundleDetailsData?.data?.isVisible ? ButtonVariants.Secondary : ButtonVariants.Primary}
                onClick={openVisibilityModal}
                disabled={!editButtonIsVisible}
              >
                {t('editBundle.bundleDetails.setToVisibleButton', {
                  visible: bundleDetailsData?.data?.isVisible ? t('common.invisible') : t('common.visible'),
                })}
              </Button>
              <Button
                variant={bundleDetailsData?.data?.isLocked ? ButtonVariants.Primary : ButtonVariants.Secondary}
                onClick={() => setShowLockBundleModal(true)}
              >
                {bundleDetailsData?.data?.isLocked ? t('common.bundle.unlockBundle') : t('common.bundle.lockBundle')}
              </Button>
            </div>
          </div>
          <Card className={styles.card}>
            <div className={styles.rowGroup}>
              <div className={styles.row}>
                <div>
                  <img
                    src={BundleDetailsPlaceholder}
                    alt={t('editBundle.bundleDetails.bundleImagePlaceholderAlt')}
                    aria-label={t('editBundle.bundleDetails.bundleImagePlaceholderAlt')}
                  />
                </div>
                <div className={styles.bundleDetailsContainer}>
                  <div className={styles.bundleDetails}>
                    <div className={styles.displayName}>{t('editBundle.bundleDetails.displayNameLabel')}: </div>
                    {bundleDetailsData?.data && <div>{displayName}</div>}
                  </div>
                  <div className={styles.bundleDetails}>
                    <div className={styles.bundleCode}>{t('editBundle.bundleDetails.bundleCodeLabel')}:</div>
                    {bundleDetailsData?.data && <div>{bundleCode}</div>}
                  </div>
                  <div className={styles.bundleDetails}>
                    <div className={styles.bundleDesc}>{t('editBundle.bundleDetails.bundleDescLabel')}:</div>
                    {bundleDetailsData?.data && <div>{bundleDesc}</div>}
                  </div>
                  <div className={styles.bundleDetails}>
                    <div className={styles.availablePrices}>{t('editBundle.bundleDetails.pricesLabel')}:</div>
                    {bundleDetailsData?.data && (
                      <div>
                        {!isMissingPricePoints ? (
                          formattedAvailablePrices
                        ) : (
                          <span className={styles.missingPricePointsMessage}>
                            <Icon iconName="Info" className={styles.iconButton} style={{ paddingLeft: 0 }} />
                            <span> {t('common.bundle.missingPricePointsLabel')} </span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={styles.bundleDetails}>
                    <div className={styles.recommendedPrices}>
                      {t('editBundle.bundleDetails.recommendedPricesLabel')}:
                    </div>
                    {bundleDetailsData?.data && <div>{formattedRecommendedPrices}</div>}
                  </div>
                  <div className={styles.bundleDetails}>
                    <div className={styles.occasion}>{t('editBundle.bundleDetails.occasionLabel')}:</div>
                    {bundleDetailsData?.data && <div>{occasion}</div>}
                  </div>
                  {bundleDetailsData?.data.bundleLinkedToDto && (
                    <div className={styles.bundleDetails}>
                      <div className={styles.occasion}>Linked To:</div>
                      <div>
                        {bundleDetailsData?.data?.bundleLinkedToDto?.map((link, index) => (
                          <>
                            {index > 0 && ', '}
                            <Link to={`/bundles/${link.id}`} key={link.id} className={styles.textbuttonDetail}>
                              {link.code}
                            </Link>
                          </>
                        ))}
                      </div>
                    </div>
                  )}
                  {lockedSettingsDescription && (
                    <div className={styles.bundleDetails}>
                      <div className={styles.occasion}>{t('editBundle.bundleDetails.lockedSettingsLabel')}:</div>
                      {bundleDetailsData?.data && <div>{lockedSettingsDescription}</div>}
                    </div>
                  )}
                </div>
              </div>
              <Button variant={ButtonVariants.Text} onClick={handleEditClick}>
                {t('common.bundle.editBundle')}
              </Button>
              <Button variant={ButtonVariants.Text} onClick={viewNavigationCodes}>
                {t('common.bundle.viewNavCode')}
              </Button>
            </div>
            <h3>{t('editBundle.bundleDetails.bundleProductChoices.header')}</h3>
            <div className={styles.tableContainer}>
              <Table
                isLoading={bundleDetailsLoading}
                loadingRowCount={5}
                includeMinWidth={false}
                data={productChoicesData || []}
                columns={[
                  {
                    accessorKey: 'choiceName',
                    header: t('editBundle.bundleDetails.bundleProductChoices.table.choiceName'),
                    enableSorting: true,
                  },
                  {
                    accessorKey: 'defaultProduct',
                    header: t('editBundle.bundleDetails.bundleProductChoices.table.defaultProduct'),
                    enableSorting: true,
                  },
                  {
                    accessorKey: 'defaultVariant',
                    header: t('editBundle.bundleDetails.bundleProductChoices.table.defaultVariant'),
                    enableSorting: true,
                  },
                ]}
              />
            </div>
          </Card>
        </div>
        <FreezeDetails bundleId={bundleId} bundleData={bundleDetailsData?.data} openCreateWizard={openCreateWizard} />

        <div className={styles.pageContainer}>
          <div className={styles.summaryBlocks}>
            <BundleSummaryBlock
              variant={SummaryBlockVariants.Green}
              blocks={[
                {
                  count: bundleStatisticsData?.data.recommendedPriceStoreCount,
                  label: t('editBundle.priceStats.usingRecommendedPricesLabel'),
                },
              ]}
              title="Price Stats"
            />
            <BundleSummaryBlock
              variant={SummaryBlockVariants.LightBlue}
              blocks={[
                {
                  count: bundleStatisticsData?.data.nonRecommendedPriceStoreCount,
                  label: t('editBundle.priceStats.usingOtherPricesLabel'),
                },
              ]}
            />
          </div>
          <Card className={styles.secondCard}>
            <Table
              width="calc(80vw - 250px)"
              className={styles.tableCard}
              enableRowCount
              data={allPricesData || []}
              columns={[
                { accessorKey: 'price', header: t('editBundle.priceStats.table.price'), enableSorting: true },
                {
                  accessorKey: 'storeCount',
                  header: t('editBundle.priceStats.table.numStores'),
                  enableSorting: true,
                  cell: storeCountCell(openModalStoreList),
                },
                { accessorKey: 'type', header: t('editBundle.priceStats.table.type'), enableSorting: true },
              ]}
              isLoading={bundleStatisticsLoading}
              returnedRecordsText={(count) => t('editBundle.priceStats.table.returnedRecord', { count })}
            />
          </Card>
        </div>

        <CreateFreeze
          bundleData={bundleDetailsData?.data}
          isOpen={showCreateWizard}
          handleClose={closeCreateWizard}
          setFreezeName={setFreezeName}
          setConfirmationModalVisible={setConfirmationModalVisible}
        />
        <ConfirmationModal
          title={t('editBundle.freezes.freezeCreatedSuccessfully', { freezeName })}
          buttonText={t('editBundle.freezes.backToBundleButton')}
          isOpen={confirmationModalVisible}
          onClose={() => setConfirmationModalVisible(false)}
          hideStatusUpdateText
        />
        <SetVisibleModal
          headerText={bundleDetailsData?.data?.isVisible ? t('common.setToInvisible?') : t('common.setToVisible?')}
          confirmText={bundleDetailsData?.data?.isVisible ? t('common.setToInvisible') : t('common.setToVisible')}
          bodyText={
            bundleDetailsData?.data?.isVisible
              ? t('editBundle.visibleModal.setInvisibleText')
              : t('editBundle.visibleModal.setVisibleText')
          }
          isOpen={showVisibilityModal}
          onClose={closeVisibilityModal}
          onConfirm={handleSetVisibilityClick}
          isLoading={updateBundleVisibilityLoading}
        />
        <LockBundleModal
          headerText={
            bundleDetailsData?.data?.isLocked ? t('common.bundle.unlockBundle?') : t('common.bundle.lockBundle?')
          }
          confirmText={
            bundleDetailsData?.data?.isLocked ? t('common.bundle.unlockBundle') : t('common.bundle.lockBundle')
          }
          bodyText={
            bundleDetailsData?.data?.isLocked
              ? t('editBundle.lockModal.unlockBundleText')
              : t('editBundle.lockModal.lockBundleText')
          }
          isOpen={showLockBundleModal}
          onClose={() => setShowLockBundleModal(false)}
          onConfirm={handleLockBundleClick}
          isLoading={updateBundleLockLoading}
        />

        <EditLinkedBundlesModal
          isOpen={isEditLinkedBundlesModalOpen}
          onClose={handleLinkedBundleClose}
          detail={bundleDetailsData?.data?.bundleLinkedToDto || []}
          bundleName={bundleDetailsData?.data?.linkedBundleName || ''}
        />

        <StorePricesModal
          isOpen={pricesModalConfig.openModal}
          bundleId={bundleId!}
          onClose={() => setPricesModalConfig({ openModal: false, pricePoint: '' })}
          pricePoint={pricesModalConfig.pricePoint}
        />
      </div>
    </>
  );
};

export default BundleDetails;
